/**
 * Firebase initialisation.
 *
 * The config below is not a secret. Firebase is designed for it to ship inside the
 * public JavaScript bundle: it identifies the project, it does not grant access to it.
 * Security comes from the rules in `firestore.rules` and `storage.rules`
 * (ARCHITECTURE.md §8.3, FR-NFR-04).
 *
 * The things that ARE secret — the service-account key for the backup job, any Telegram
 * bot token — live only in Cloud Run's runtime and never reach this file.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  connectAuthEmulator,
  getAuth,
  type Auth,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: 'AIzaSyDMZJjUH78ccjziBKc1IqKBK7eLXJ_nNWY',
  authDomain: 'expense-tracker-a6541.firebaseapp.com',
  projectId: 'expense-tracker-a6541',
  storageBucket: 'expense-tracker-a6541.firebasestorage.app',
  messagingSenderId: '295369218803',
  appId: '1:295369218803:web:5edcb6af2226ba5f3c3586',
};

export const app: FirebaseApp = initializeApp(firebaseConfig);

/**
 * In development, talk to the emulator suite rather than the real project.
 *
 * This is what keeps `npm run dev` from writing test data into the live database —
 * a mistake that is tedious to unpick once real transactions are in there.
 */
const usingEmulators =
  import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS !== 'false';

/**
 * **The database is named, not `(default)`.**
 *
 * `expense-tracker-a6541` holds one Firestore database called `expense-tracker-db`, and
 * there is no `(default)` in the project at all. Omitting the id — which is what every
 * Firebase example does — connects to `(default)` and fails to find anything.
 *
 * This was invisible for nine milestones because the emulator serves `(default)` and
 * everything was tested against it. The emulator keeps that name (the rules-unit-testing
 * harness has no way to target another), so the two differ by a string and nothing else:
 * both go through the same three-argument `initializeFirestore`, so the named-database
 * code path is the one exercised either way.
 */
export const DATABASE_ID = usingEmulators ? '(default)' : 'expense-tracker-db';

/**
 * Offline persistence is switched on at construction, not bolted on afterwards
 * (FR-PLT-04). `persistentMultipleTabManager` keeps two open tabs from fighting over
 * the same local cache — without it the second tab silently loses persistence.
 */
export const db: Firestore = initializeFirestore(
  app,
  { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) },
  DATABASE_ID,
);

export const auth: Auth = getAuth(app);

/**
 * Storage is loaded on demand, not at startup.
 *
 * Nothing touches it until attachments arrive at M9, and importing it eagerly puts
 * its whole module in the initial bundle for every page load in the meantime. The
 * dynamic import here is the same technique the export libraries will use
 * (FR-RPT-12).
 */
export async function getStorageLazy() {
  const { connectStorageEmulator, getStorage } = await import('firebase/storage');
  const storage = getStorage(app);
  if (usingEmulators) connectStorageEmulator(storage, '127.0.0.1', 9199);
  return storage;
}

if (usingEmulators) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
}

export const PROJECT_ID = firebaseConfig.projectId;
